<template>
  <main aria-labelledby="user-requests-title"
        class="user-requests-page page-content-inner"
  >
    <CompanyBreadcrumb :aria-label="$t('general.user_requests')"
                       :home-aria-label="$t('general.home')"
                       :items="breadcrumbItems"
    />

    <CompanyPageHeading :count="normalizedRequestCount"
                        :title="$t('general.user_requests')"
                        title-id="user-requests-title"
    />

    <section class="section-header user-requests-toolbar pb-0">
      <CompanySearchInput v-model="searchQuery"
                          :placeholder="$t('general.search_in_user_requests')"
                          @input="isTyping = true"
      />

      <div class="section-header-right user-requests-sort">
        <multiple-select-input :allow-empty="false"
                               :model.sync="selectedOrderBy"
                               :show-labels="false"
                               :values="orderByOptions"
                               class="request-order-select"
                               label="label"
                               track-by="value"
                               @select="onOrderByChange"
        />
      </div>
    </section>

    <span v-if="isLoading" class="sr-only" role="status">
      {{ $t("general.loading") }}
    </span>

    <DefaultLoading v-if="isLoading" />

    <section v-else-if="requestItems.length"
             :aria-label="$t('general.user_requests')"
             class="user-requests-grid"
    >
      <UserRequestCard v-for="item in requestItems"
                       :key="'training_list_item_' + item.id"
                       :item="item"
                       @select="handleOnClickCard"
      />
    </section>

    <div v-else-if="hasLoaded" class="user-requests-empty" role="status">
      <i aria-hidden="true" class="uil-search-alt"></i>
      <p>{{ $t("general.no_records_found") }}</p>
    </div>

    <Pagination v-if="hasLoaded && shouldShowPagination"
                :page.sync="pageProxy"
                :paginate="items"
    />
  </main>
</template>

<script>
import debounce from "lodash/debounce";
import MultipleSelectInput from "@/assets/components/inputs/MultipleSelectInput.vue";
import Pagination from "@/view/components/dataTable/Pagination";
import CompanyBreadcrumb from "@/view/company/components/breadcrumb/CompanyBreadcrumb.vue";
import CompanyPageHeading from "@/view/company/components/header/CompanyPageHeading.vue";
import CompanySearchInput from "@/view/company/components/search/CompanySearchInput.vue";
import DefaultLoading from "@/view/components/loading/DefaultLoading";
import UserRequestCard from "@/view/company/RequestsAndDemands/components/UserRequestCard.vue";
import { GET_ITEMS } from "@/core/services/store/REST.module";

import trainingModule, {
  MODULE_NAME as TRAINING_MODULE_NAME,
  REQUEST_COUNT as TRAINING_USER_REQUEST_COUNT,
} from "@/core/services/store/training.module";
import store from "@/core/services";

const _TRAINING_MODULE_NAME = TRAINING_MODULE_NAME;

export default {
  name: "EducationRequestList",
  components: {
    CompanyBreadcrumb,
    CompanyPageHeading,
    CompanySearchInput,
    DefaultLoading,
    MultipleSelectInput,
    Pagination,
    UserRequestCard,
  },
  beforeCreate() {
    function registerStoreModule(moduleName, storeModule) {
      if (!(store && store.state && store.state[moduleName])) {
        store.registerModule(moduleName, storeModule);
      }
    }

    registerStoreModule(_TRAINING_MODULE_NAME, trainingModule);
  },
  data() {
    return {
      searchQuery: "",
      selectedOrderBy: null,
      isTyping: false,
      items: {
        data: [],
        last_page: 1,
      },
      latestRequestId: 0,
      pendingRequestCount: 0,
      hasLoaded: false,
      restUrl: "api/training-requests",
      filters: {
        page: 1,
        search: "",
        order_by: "name",
        sort: "desc",
        per_page: 16,
        with_requests: true,
      },
    };
  },
  computed: {
    userRequestCount() {
      return store.getters[
      _TRAINING_MODULE_NAME + "/" + TRAINING_USER_REQUEST_COUNT
          ];
    },
    normalizedRequestCount() {
      return Number(this.userRequestCount) || 0;
    },
    breadcrumbItems() {
      return [
        { name: this.$t("general.requests_my_demands") },
        { name: this.$t("general.user_requests") },
      ];
    },
    orderByOptions() {
      return [
        { value: "name", label: this.$t("filter.Filter_by_name") },
        {
          value: "training_type_id",
          label: this.$t("filter.Filter_by_category"),
        },
        { value: "created_at", label: this.$t("filter.Filter_by_date") },
      ];
    },
    isLoading() {
      return this.pendingRequestCount > 0;
    },
    requestItems() {
      return this.items && Array.isArray(this.items.data)
          ? this.items.data.filter(Boolean)
          : [];
    },
    shouldShowPagination() {
      return Boolean(
          this.items && this.items.data && Number(this.items.last_page) > 1
      );
    },
    pageProxy: {
      set(value) {
        this.filters.page = value;
        this.applyFilter();
      },
      get() {
        return this.filters.page;
      },
    },
  },
  methods: {
    handleOnClickCard(payload) {
      this.$router.push({
        name: "company.requests.training",
        params: { id: payload },
      });
    },
    onOrderByChange(option) {
      this.selectedOrderBy = option;
      this.filters.page = 1;
      this.applyFilter();
    },
    async applyFilter() {
      const requestId = ++this.latestRequestId;
      this.pendingRequestCount += 1;
      this.$set(this.filters, "search", this.searchQuery);
      this.$set(
          this.filters,
          "order_by",
          this.selectedOrderBy ? this.selectedOrderBy.value : "name"
      );

      try {
        const result = await this.$store.dispatch(GET_ITEMS, {
          url: this.restUrl,
          acceptPromise: true,
          filters: { ...this.filters },
        });

        if (requestId === this.latestRequestId && result && result.status) {
          this.items = result.data;
        }
      } finally {
        this.pendingRequestCount = Math.max(0, this.pendingRequestCount - 1);
        if (requestId === this.latestRequestId) {
          this.hasLoaded = true;
        }
      }
    },
  },
  watch: {
    searchQuery: debounce(function () {
      this.isTyping = false;
    }, 1000),
    isTyping(value) {
      if (!value) {
        this.filters.page = 1;
        this.applyFilter();
      }
    },
  },
  mounted() {
    if (!this.CatalogTrainingVisible) {
      return;
    }
    this.selectedOrderBy = this.orderByOptions[0];
    this.applyFilter();
  },
};
</script>

<style scoped>
.user-requests-page {
  --request-primary: var(--primaryColor, #3e416d);
  --request-text: #202334;
  --request-muted: #697386;
  --request-border: #e5e8ef;
  --request-surface: #ffffff;
  --request-soft: #f6f7fb;
  margin-top: 24px;
  padding: 0 16px 32px;
}

.user-requests-toolbar {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
  padding: 0;
  border: 0;
}

.user-requests-sort {
  display: flex;
  flex: 0 0 240px;
  width: 240px;
  min-width: 0;
  align-items: center;
}

.request-order-select {
  width: 100%;
  min-width: 0;
  margin-bottom: 0;
}

::v-deep .request-order-select.form-group,
::v-deep .request-order-select.form-group > .custom-multiselect {
  width: 100%;
  height: 48px !important;
  min-height: 48px;
  margin-bottom: 0 !important;
}

::v-deep .request-order-select .multiselect__tags {
  display: flex;
  height: 48px;
  min-height: 48px;
  align-items: center;
  box-sizing: border-box;
  padding: 12px 40px 12px 16px;
  border: 1px solid #cfd5df;
  border-radius: 10px;
  background: #ffffff;
  font-size: 16px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

::v-deep .request-order-select .multiselect--active .multiselect__tags {
  border-color: var(--request-primary);
  box-shadow: 0 0 0 3px rgba(62, 65, 109, 0.16);
}

::v-deep .request-order-select .multiselect__single {
  overflow: hidden;
  min-width: 0;
  max-width: 100%;
  margin: 0;
  padding: 0;
  color: var(--request-text);
  font-size: 16px;
  line-height: 1.5;
  text-overflow: ellipsis;
  white-space: nowrap;
}

::v-deep .request-order-select .multiselect__select {
  width: 40px;
  height: 46px;
}

::v-deep .request-order-select .multiselect__content-wrapper {
  overflow: hidden;
  margin-top: 4px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

::v-deep .request-order-select .multiselect__option {
  display: flex;
  min-height: 48px;
  align-items: center;
  box-sizing: border-box;
  padding: 12px 16px;
  color: #334155;
  font-size: 16px;
}

::v-deep .request-order-select .multiselect__option--highlight {
  background: #f1f5f9;
  color: #334155;
}

::v-deep .request-order-select .multiselect__option--selected,
::v-deep
.request-order-select
.multiselect__option--selected.multiselect__option--highlight {
  background: var(--request-primary);
  color: #ffffff;
  font-weight: 500;
}

.user-requests-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 24px;
}

.user-requests-empty {
  display: flex;
  min-height: 240px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 32px 16px;
  border: 1px dashed #cfd5df;
  border-radius: 14px;
  background: var(--request-soft);
  color: var(--request-muted);
  text-align: center;
}

.user-requests-empty i {
  margin-bottom: 8px;
  font-size: 32px;
}

.user-requests-empty p {
  margin: 0;
  font-size: 16px;
  line-height: 1.5;
}

@media (max-width: 1199px) {
  .user-requests-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 959px) {
  .user-requests-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 767px) {
  .user-requests-page {
    margin: 0;
    padding: 0;
  }

  .user-requests-toolbar {
    gap: 8px;
    margin-bottom: 16px;
  }

  .user-requests-sort {
    flex-basis: 144px;
    width: 144px;
  }

  .user-requests-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
  }
}

@media (max-width: 359px) {
  .user-requests-toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .user-requests-sort {
    flex-basis: auto;
    width: 100%;
  }
}

@media (prefers-reduced-motion: reduce) {
  ::v-deep .request-order-select .multiselect__tags {
    transition: none;
    animation: none;
  }
}
</style>
